import React, { useState, useEffect, useMemo, memo } from "react";
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Pagination,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Autocomplete,
  Badge,
  Card,
  Divider,
  Chip,
  Stack,
} from "@mui/material";

import {
  Search as SearchIcon,
  ViewList,
  GridView,
  FilterList,
  Clear,
  Save,
} from "@mui/icons-material";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StarIcon from "@mui/icons-material/Star";

import { useTheme, alpha } from '@mui/material/styles';
import { useMutation, useQuery } from "react-query";
import client from "../../../API";
import CreatorContainers from "./creatorcontainersearch";
import PromoPlatFilter from "./Filters/PromoPlatFilter";
import NichesFilter from "./Filters/NichesFilters";

import {
  headerToKeyMap,
  indexToPromoFilter,
  platformToKey,
  platPromoToKey,
  platPromoToHead,
  headers,
  calculateTotalFollowing,
  calculateAverageCreatorRate,
} from "../../../Utils/constants";
import { StyledTableRow } from "../../../Utils/styledcell";
import AllFilters from "./Filters/allFilters";
import MinMaxFilter from "./Filters/MinMaxFilter";
import ChatModalCreator from "../../../Components/ChatComponents/ChatModalCreators";

const LeftColumnSearch = memo(function LeftColumnSearch({
  onCreatorSelect,
  selectedItems,
  data,
  loading,
  onInviteToPartnership = null,
  itemsPerPage = 15,
  onChangePromo,
  initialFilters = null,
  initialCollectionId = "",
}) {
  const theme = useTheme();
  
  // -------------------------
  // 1) Basic filter states
  // -------------------------
  const [promoIndex, setPromoIndex] = useState(1);
  const [nicheSet, setNicheSet] = useState(new Set([]));
  const [creatorGender, setCreatorGender] = useState(new Set([]));
  const [creatorRace, setCreatorRace] = useState(new Set([]));
  const [creatorRegion, setCreatorRegion] = useState(new Set([]));
  const [publicGender, setPublicGender] = useState(new Set([]));
  const [publicAge, setPublicAge] = useState(new Set([]));
  const [publicCountry, setPublicCountry] = useState(new Set([]));

  // Range filters (rate, followers, views)
  const [minRate, setMinRate] = useState(null);
  const [maxRate, setMaxRate] = useState(null);
  const [minFollow, setMinFollow] = useState(null);
  const [maxFollow, setMaxFollow] = useState(null);
  const [minViews, setMinViews] = useState(null);
  const [maxViews, setMaxViews] = useState(null);

  // UI states
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [allFiltersOpen, setAllFiltersOpen] = useState(false);
  const [chatCreatorId, setChatCreatorId] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  const handleChatWithCreator = (creatorId) => {
    setChatCreatorId(creatorId);
    setChatOpen(true);
  };

  // ----------------------------------------
  // 2) Collections + popular/recent searches
  // ----------------------------------------
  const { data: collections = [], refetch: refetchCollections } = useQuery(
    "collections",
    () => client.collections.list()
  );

  const {
    data: allSearches,
    isLoading: allSearchesLoading,
  } = useQuery("allSearches", () => client.collections.popular(), {
    staleTime: 60_000,
  });

  const [autoOptions, setAutoOptions] = useState([]);
  useEffect(() => {
    if (!allSearches) return;
    const { popular = [], recent = [] } = allSearches;

    const recentOpts = recent.map((r) => ({
      label: r.name,
      type: "recent",
      collection: r,
    }));
    const popularOpts = popular.map((p) => ({
      label: p.name,
      type: "popular",
    }));

    setAutoOptions([...recentOpts, ...popularOpts]);
  }, [allSearches]);

  const [selectedCollection, setSelectedCollection] = useState("");
  const [isAddCollectionDialogOpen, setIsAddCollectionDialogOpen] =
    useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");

  const { mutate: createCollection } = useMutation(client.collections.create, {
    onSuccess: (newColl) => {
      refetchCollections();
      setNewCollectionName("");
      setSelectedCollection(newColl.id);
    },
  });

  const { mutate: deleteCollection } = useMutation(client.collections.delete, {
    onSuccess: (data) => {
      refetchCollections();
      setSelectedCollection("");
    },
  });

  // -------------------------------------------------
  // 3) AI Search: triggered on "Enter" or on "popular"
  // -------------------------------------------------
  const { mutate: aiSearchMutation, isLoading: aiSearching } = useMutation(
    async (queryText) => {
      const res = await client.creators.search({ query: queryText });
      return res;
    },
    {
      onError: (err) => console.error("AI search error:", err),
      onSuccess: (searchResult) => {
        refetchCollections();
        setSelectedCollection(searchResult.collection_id);
      },
    }
  );

  // ----------------------------------------
  // 4) Single local search text (freeSolo)
  // ----------------------------------------
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && searchQuery.trim().length > 0) {
      aiSearchMutation(searchQuery);
      setSearchQuery("");
    }
  };

  const handleAutoCompleteChange = (event, newVal) => {
    if (!newVal) return;
    if (typeof newVal === "string") {
      return;
    }
    if (newVal.type === "popular") {
      aiSearchMutation(newVal.label);
      setSearchQuery("");
    } else if (newVal.type === "recent") {
      setSelectedCollection(newVal.collection.id);
      setSearchQuery("");
    }
  };

  // ---------------------------------------------------
  // 5) If there's an initial collection, apply it once
  // ---------------------------------------------------
  useEffect(() => {
    if (initialCollectionId) {
      setSelectedCollection(initialCollectionId);
    }
  }, [initialCollectionId]);

  // ----------------------------------------------------
  // 6) If there are initial filters, apply them once
  // ----------------------------------------------------
  useEffect(() => {
    if (!initialFilters) return;

    const {
      promoIndex: colPromoIndex,
      nicheSet: colNicheSet,
      creatorGender: colCreatorGender,
      creatorRace: colCreatorRace,
      creatorRegion: colCreatorRegion,
      publicGender: colPublicGender,
      publicAge: colPublicAge,
      publicCountry: colPublicCountry,
      minPrice: colMinPrice,
      maxPrice: colMaxPrice,
      minFollow: colMinFollow,
      maxFollow: colMaxFollow,
      minViews: colMinViews,
      maxViews: colMaxViews,
    } = initialFilters;

    setPromoIndex(colPromoIndex ?? -1);
    onChangePromo?.(colPromoIndex ?? -1);

    setNicheSet(new Set(colNicheSet ?? []));
    setCreatorGender(new Set(colCreatorGender ?? []));
    setCreatorRace(new Set(colCreatorRace ?? []));
    setCreatorRegion(new Set(colCreatorRegion ?? []));
    setPublicGender(new Set(colPublicGender ?? []));
    setPublicAge(new Set(colPublicAge ?? []));
    setPublicCountry(new Set(colPublicCountry ?? []));
    setMinRate(colMinPrice ?? null);
    setMaxRate(colMaxPrice ?? null);
    setMinFollow(colMinFollow ?? null);
    setMaxFollow(colMaxFollow ?? null);
    setMinViews(colMinViews ?? null);
    setMaxViews(colMaxViews ?? null);
  }, [initialFilters, onChangePromo]);

  // --------------------------------------------------------------------
  // 7) Whenever `selectedCollection` changes, load those collection filters
  //    + clear local query so we "push" the new search
  // --------------------------------------------------------------------
  useEffect(() => {
    if (!selectedCollection) return;
    const coll = collections.find((c) => c.id === selectedCollection);
    if (!coll || !coll.filters) return;

    const {
      promoIndex: colPromoIndex,
      nicheSet: colNicheSet,
      creatorGender: colCreatorGender,
      creatorRace: colCreatorRace,
      creatorRegion: colCreatorRegion,
      publicGender: colPublicGender,
      publicAge: colPublicAge,
      publicCountry: colPublicCountry,
      minPrice: colMinPrice,
      maxPrice: colMaxPrice,
      minFollow: colMinFollow,
      maxFollow: colMaxFollow,
      minViews: colMinViews,
      maxViews: colMaxViews,
    } = coll.filters;

    setPromoIndex(colPromoIndex ?? -1);
    onChangePromo?.(colPromoIndex ?? -1);

    setNicheSet(new Set(colNicheSet ?? []));
    setCreatorGender(new Set(colCreatorGender ?? []));
    setCreatorRace(new Set(colCreatorRace ?? []));
    setCreatorRegion(new Set(colCreatorRegion ?? []));
    setPublicGender(new Set(colPublicGender ?? []));
    setPublicAge(new Set(colPublicAge ?? []));
    setPublicCountry(new Set(colPublicCountry ?? []));
    setMinRate(colMinPrice ?? null);
    setMaxRate(colMaxPrice ?? null);
    setMinFollow(colMinFollow ?? null);
    setMaxFollow(colMaxFollow ?? null);
    setMinViews(colMinViews ?? null);
    setMaxViews(colMaxViews ?? null);

    setSearchQuery("");
    setPage(0);
  }, [selectedCollection, collections, onChangePromo]);

  // ----------------------------------------
  // 8) Reset page when any filters change
  // ----------------------------------------
  useEffect(() => {
    setPage(0);
  }, [
    promoIndex,
    searchQuery,
    nicheSet,
    minRate,
    maxRate,
    minFollow,
    maxFollow,
    minViews,
    maxViews,
    creatorGender,
    creatorRace,
    creatorRegion,
    publicGender,
    publicAge,
    publicCountry,
  ]);

  const activeFiltersCount = (()=>{
    let count = 0;
    
    if(minFollow!==null && maxFollow !==null)
      count++;
    if(minViews!==null && maxViews !==null)
      count++;
    if(creatorGender.size > 0)
      count++;
    if(creatorRace.size > 0)
      count++;
    if(creatorRegion.size > 0)
      count++;
    if(publicGender.size > 0)
      count++;
    if(publicCountry.size > 0)
      count++;
    if(publicAge.size > 0)
      count++;

    return count;
  })();

  // ----------------------------------------
  // 9) Reset all filters
  // ----------------------------------------
  const handleResetFilters = () => {
    setPromoIndex(-1);
    onChangePromo?.(-1);
    setNicheSet(new Set([]));
    setCreatorGender(new Set([]));
    setCreatorRace(new Set([]));
    setCreatorRegion(new Set([]));
    setPublicGender(new Set([]));
    setPublicAge(new Set([]));
    setPublicCountry(new Set([]));
    setMinRate(null);
    setMaxRate(null);
    setMinFollow(null);
    setMaxFollow(null);
    setMinViews(null);
    setMaxViews(null);
    setSearchQuery("");
    setSelectedCollection("");
    setPage(0);
  };

  // ------------------------------------------------
  // 10) Save current filters into a new collection
  // ------------------------------------------------
  const handleSaveCollection = () => {
    const filters = {
      promoIndex,
      nicheSet: Array.from(nicheSet),
      creatorGender: Array.from(creatorGender),
      creatorRace: Array.from(creatorRace),
      creatorRegion: Array.from(creatorRegion),
      publicGender: Array.from(publicGender),
      publicAge: Array.from(publicAge),
      publicCountry: Array.from(publicCountry),
      minPrice: minRate,
      maxPrice: maxRate,
      minFollow,
      maxFollow,
      minViews,
      maxViews,
    };
    createCollection({ name: newCollectionName, filters });
  };

  const handleMinMaxApply = (value, minSetter, maxSetter) => {
    minSetter(value.min);
    maxSetter(value.max);
  };

  // --------------------------
  // 11) Filter logic
  // --------------------------
  const parseFollowerCount = (val) => {
    if (!val || val === "N/A") return 0;
    if (Number.isFinite(val)) return val;
    return parseInt(val.replace(/,/g, ""), 10) || 0;
  };
  const parseViews = (v) => parseInt((v || "").replace(/,/g, ""), 10) || 0;
  const priceRangeMatch = (p) => p >= (minRate ?? p) && p <= (maxRate ?? p);
  const followerRangeMatch = (fc) =>
    fc >= (minFollow ?? fc) && fc <= (maxFollow ?? fc);

  const emptyCountry = { code: "N/A", value: Number.NEGATIVE_INFINITY };
  const emptyAge = { age_range: "N/A", value: Number.NEGATIVE_INFINITY };
  const emptyGender = { name: "N/A", value: Number.NEGATIVE_INFINITY };

  function highestValueCountry(publicData) {
    return publicData.reduce((max, demo) => {
      const localMax = demo.country_data?.reduce(
        (ctrMax, c) => (c.value > ctrMax.value ? c : ctrMax),
        emptyCountry
      );
      return localMax?.value > max.value ? localMax : max;
    }, emptyCountry);
  }

  function higherValueAge(publicData) {
    const sumsPerDemo = publicData.map((demo) =>
      demo.audience_data?.reduce((acc, entry) => {
        const { age_range, value } = entry;
        const existing = acc.find((x) => x.age_range === age_range);
        if (existing) {
          existing.value += Math.round(value);
        } else {
          acc.push({ age_range, value: Math.round(value) });
        }
        return acc;
      }, [])
    );
    return sumsPerDemo.reduce((globalMax, singleDemo) => {
      if (!singleDemo) return globalMax;
      const localMax = singleDemo.reduce(
        (ageMax, group) => (group.value > ageMax.value ? group : ageMax),
        emptyAge
      );
      return localMax.value > globalMax.value ? localMax : globalMax;
    }, emptyAge);
  }

  function higherValueGender(publicData) {
    const sumsPerDemo = publicData.map((demo) => {
      if (!demo.audience_data) return [];
      return [
        {
          name: "male",
          value: demo.audience_data.reduce(
            (acc, i) =>
              i.gender?.toLowerCase() === "male"
                ? acc + Math.round(i.value)
                : acc,
            0
          ),
        },
        {
          name: "female",
          value: demo.audience_data.reduce(
            (acc, i) =>
              i.gender?.toLowerCase() === "female"
                ? acc + Math.round(i.value)
                : acc,
            0
          ),
        },
      ];
    });
    return sumsPerDemo.reduce((globalMax, singleDemo) => {
      const localMax = singleDemo.reduce(
        (gMax, g) => (g.value > gMax.value ? g : gMax),
        emptyGender
      );
      return localMax.value > globalMax.value ? localMax : globalMax;
    }, emptyGender);
  }

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    const { platform, promo } = indexToPromoFilter(promoIndex) || {};

    if (
      promoIndex !== -1 &&
      (!platformToKey[platform] || !platPromoToKey[platform])
    ) {
      return [];
    }

    return data.filter((creator) => {
      if (promoIndex !== -1) {
        const platformKeys = platformToKey[platform];
        const hasPlatformData = platformKeys.some(
          (k) => creator[k] && creator[k] !== "N/A"
        );
        if (!hasPlatformData) return false;
      }

      let promoKey = null;
      if (promoIndex !== -1) {
        promoKey = platPromoToKey[platform][promo];
        if (!creator[promoKey]) {
          return false;
        }
      }

      const passesNiche =
        nicheSet.size > 0
          ? creator.primary_markets?.some((m) => nicheSet.has(m))
          : true;

      const passesRace =
        creatorRace.size > 0
          ? Array.from(creatorRace).some((r) => creator.ethnicity?.includes(r))
          : true;
      const passesGender =
        creatorGender.size > 0
          ? Array.from(creatorGender).some((g) =>
            (creator.gender || []).includes(g)
          )
          : true;
      const passesRegion =
        creatorRegion.size > 0 ? creatorRegion.has(creator.region) : true;

      let rawRate = null;
      if (promoIndex === -1) {
        rawRate = calculateAverageCreatorRate(creator);
      } else {
        rawRate = creator[promoKey];
      }
      const passesRate =
        minRate != null && maxRate != null ? priceRangeMatch(rawRate) : true;

      let followerCount;
      if (promoIndex === -1) {
        followerCount = parseFollowerCount(calculateTotalFollowing(creator));
      } else {
        const keyForFollowers = platformToKey[platform][0];
        followerCount = parseFollowerCount(creator[keyForFollowers]);
      }
      const passesFollowers =
        minFollow != null && maxFollow != null
          ? followerRangeMatch(followerCount)
          : true;

      let passesViews = true;
      if (minViews != null && maxViews != null) {
        const val = parseViews(creator.avg_views);
        passesViews = val >= minViews && val <= maxViews;
      }

      let passDemoCountry = true,
        passDemoAge = true,
        passDemoGender = true;
      if (Array.isArray(creator.public)) {
        if (publicCountry.size > 0) {
          const topCountry = highestValueCountry(creator.public);
          passDemoCountry = publicCountry.has(topCountry.code);
        }
        if (publicAge.size > 0) {
          const topAge = higherValueAge(creator.public);
          passDemoAge = publicAge.has(topAge.age_range.toLowerCase());
        }
        if (publicGender.size > 0) {
          const topG = higherValueGender(creator.public);
          passDemoGender = publicGender.has(topG.name);
        }
      }

      let passesText = true;
      if (searchQuery.trim().length > 0) {
        const low = searchQuery.toLowerCase();
        passesText = Object.values(creator).some(
          (val) => typeof val === "string" && val.toLowerCase().includes(low)
        );
      }

      return (
        passesNiche &&
        passesRace &&
        passesGender &&
        passesRegion &&
        passesRate &&
        passesFollowers &&
        passesViews &&
        passDemoCountry &&
        passDemoAge &&
        passDemoGender &&
        passesText
      );
    });
  }, [
    data,
    promoIndex,
    nicheSet,
    minRate,
    maxRate,
    minFollow,
    maxFollow,
    minViews,
    maxViews,
    creatorGender,
    creatorRace,
    creatorRegion,
    publicGender,
    publicAge,
    publicCountry,
    searchQuery,
  ]);

  const showLocalSpinner =
    searchQuery.trim().length > 0 &&
    filteredData.length === 0 &&
    !aiSearching &&
    !loading;

  // --------------------------------------
  // 12) Table headers
  // --------------------------------------
  const genericHeaders = [
    "Creator",
    "Primary Market",
    "Followers",
    "Avg. Views",
    "Price",
    "CPM",
  ];
  let displayHeaders = genericHeaders;
  let platform = null;
  let promotionType = null;

  if (promoIndex !== -1) {
    const { platform: pf, promo } = indexToPromoFilter(promoIndex) || {};
    platform = pf;
    promotionType = promo;
    if (platform && headers[platform]) {
      displayHeaders = [...headers[platform]];
      const promoHeader = platPromoToHead[platform]?.[promotionType];
      if (promoHeader) {
        if (displayHeaders.length >= 2) {
          displayHeaders.splice(-2, 0, promoHeader);
        } else {
          displayHeaders.push(promoHeader);
        }
      }
    }
  }

  // ----------------------------------------
  // 13) Select / Invite
  // ----------------------------------------
  const handleSelectItem = (creatorId) => {
    const sel = filteredData.find((c) => c.creator === creatorId);
    if (!sel) return;

    if (promoIndex === -1) {
      const price = sel.computedPrice || null;
      const followingCount = sel.followers || 0;
      const relevantData = {
        id: sel.creator,
        name: sel.creator,
        price,
        following: followingCount,
        promotionPlatform: "",
        promotionType: "",
        platformLink: null,
        pfphref: sel.pfphref,
      };
      onCreatorSelect?.(creatorId, relevantData);
    } else {
      const { platform: pf, promo } = indexToPromoFilter(promoIndex) || {};
      if (!pf || !promo) return;

      const priceKey = platPromoToKey[pf]?.[promo];
      if (!priceKey) return;

      const rawPrice = sel[priceKey];
      const price = rawPrice != null ? rawPrice : null;

      const platformLinkKey = `${pf} Link`;
      const platformLink = sel[headerToKeyMap[platformLinkKey]];
      const followingCount = sel[platformToKey[pf][0]];

      const relevantData = {
        id: sel.creator,
        name: sel.creator,
        price,
        following: followingCount,
        promotionPlatform: pf,
        promotionType: promo,
        platformLink,
        pfphref: sel.pfphref,
      };
      onCreatorSelect?.(creatorId, relevantData);
    }
  };

  const handleInviteToPartnership = (creator) => {
    if (!onInviteToPartnership) return;
    const relevantData = {
      ...creator,
      id: creator.creator,
      name: creator.creator,
    };
    onInviteToPartnership?.(relevantData);
  };

  // ----------------------------------------
  // RENDER
  // ----------------------------------------
  return (
    <>
      {/* MODERN SEARCH & FILTERS BAR */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'visible',
          bgcolor: 'background.paper',
          background: (theme) => theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Main Search Bar */}
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 2px 8px rgba(0,0,0,0.3)'
                    : '0 2px 8px rgba(0,0,0,0.04)',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused': {
                    borderColor: 'primary.main',
                    boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                  }
                }
              }}
              freeSolo
              openOnFocus
              fullWidth
              options={autoOptions}
              inputValue={searchQuery}
              onInputChange={(event, newInputValue) => setSearchQuery(newInputValue)}
              onKeyDown={handleSearchKeyDown}
              onChange={handleAutoCompleteChange}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.label
              }
              renderOption={(props, option) => {
                if (typeof option === "string") {
                  return <li {...props}>{option}</li>;
                }
                return (
                  <li {...props}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {option.type === "recent" ? (
                        <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                      ) : (
                        <StarIcon sx={{ color: '#ffc107', fontSize: 18 }} />
                      )}
                      <Typography>{option.label}</Typography>
                    </Stack>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search creators, niches, or explore popular searches..."
                  size="large"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    ),
                    endAdornment: (
                      <>
                        {loading || aiSearching ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Filter Controls Row */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap="wrap"
          >
            <PromoPlatFilter
              value={promoIndex}
              onChangeValue={(val) => {
                setPromoIndex(val);
                onChangePromo?.(val);
              }}
            />
            
            <NichesFilter value={nicheSet} onChangeValue={setNicheSet} />
            
            <MinMaxFilter
              minValue={minRate}
              maxValue={maxRate}
              width="140px"
              name="Rate"
              onChangeValue={(value) =>
                handleMinMaxApply(value, setMinRate, setMaxRate)
              }
            />

            <Badge 
              badgeContent={activeFiltersCount} 
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  bgcolor: 'primary.main',
                  color: 'white'
                }
              }}
            >
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setAllFiltersOpen(true)}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 120,
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                All Filters
              </Button>
            </Badge>

            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => setViewMode("grid")}
                color={viewMode === "grid" ? "primary" : "default"}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: viewMode === "grid" ? 'primary.main' : 'divider',
                  bgcolor: viewMode === "grid" ? alpha(theme.palette.primary.main, 0.08) : 'transparent'
                }}
              >
                <GridView />
              </IconButton>
              
              <IconButton
                onClick={() => setViewMode("list")}
                color={viewMode === "list" ? "primary" : "default"}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: viewMode === "list" ? 'primary.main' : 'divider',
                  bgcolor: viewMode === "list" ? alpha(theme.palette.primary.main, 0.08) : 'transparent'
                }}
              >
                <ViewList />
              </IconButton>
              
              <Button 
                onClick={handleResetFilters} 
                color="error"
                variant="outlined"
                startIcon={<Clear />}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'error.light',
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 120,
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.08)
                  }
                }}
              >
                Clear All
              </Button>
            </Box>
          </Stack>

          {/* Active Filters Display */}
          {(nicheSet.size > 0 || activeFiltersCount > 0 || minRate || maxRate) && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {Array.from(nicheSet).map((niche) => (
                  <Chip
                    key={niche}
                    label={niche}
                    size="small"
                    onDelete={() => {
                      const newSet = new Set(nicheSet);
                      newSet.delete(niche);
                      setNicheSet(newSet);
                    }}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
                {(minRate && maxRate) && (
                  <Chip
                    label={`Rate: $${minRate} - $${maxRate}`}
                    size="small"
                    onDelete={() => {
                      setMinRate(null);
                      setMaxRate(null);
                    }}
                    sx={{ borderRadius: 2 }}
                  />
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </Card>

      {/* MAIN CONTENT AREA */}
      <Box sx={{ minHeight: 400 }}>
        {loading || aiSearching ? (
          <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3, bgcolor: 'background.paper' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {aiSearching ? "Running AI search..." : "Loading creators..."}
            </Typography>
          </Card>
        ) : showLocalSpinner ? (
          <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3, bgcolor: 'background.paper' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No local matches found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Press Enter to run AI search
            </Typography>
          </Card>
        ) : filteredData.length === 0 ? (
          <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3, bgcolor: 'background.paper' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No creators match your filters
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search criteria or filters
            </Typography>
          </Card>
        ) : viewMode === "grid" ? (
          <Box sx={{ px: { xs: 1, md: 0 } }}>
            <CreatorContainers
              creators={filteredData.slice(
                page * itemsPerPage,
                Math.min(page * itemsPerPage + itemsPerPage, filteredData.length)
              )}
              platform={platform}
              promotion={promotionType}
              selectedItems={selectedItems}
              onAddToCampaign={handleSelectItem}
              onInviteToPartnership={
                onInviteToPartnership ? handleInviteToPartnership : null
              }
              onChatWithCreator={handleChatWithCreator}
            />
          </Box>
        ) : (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden', bgcolor: 'background.paper' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50' }}>
                  <TableCell padding="checkbox">
                    <Typography variant="subtitle2" fontWeight={600}>Select</Typography>
                  </TableCell>
                  {displayHeaders.map((header) => (
                    <TableCell key={header}>
                      <Typography variant="subtitle2" fontWeight={600}>{header}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(
                    page * itemsPerPage,
                    Math.min(page * itemsPerPage + itemsPerPage, filteredData.length)
                  )
                  .map((creator, index) => (
                    <StyledTableRow
                      key={creator.creator}
                      selected={selectedItems.has(creator.creator)}
                      sx={{
                        '&:nth-of-type(odd)': { 
                          bgcolor: (theme) => theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.background.paper, 0.5)
                            : 'grey.25' 
                        },
                        '&:hover': { 
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                        }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedItems.has(creator.creator)}
                          onChange={() => handleSelectItem(creator.creator)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {creator.display_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {creator.primary_markets?.join(", ") || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{creator.followers}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{creator.avg_views}</Typography>
                      </TableCell>
                      {promoIndex === -1 ? (
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {creator.computedPrice ?? "N/A"}
                          </Typography>
                        </TableCell>
                      ) : (
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {(() => {
                              const { platform: pf, promo } =
                                indexToPromoFilter(promoIndex) || {};
                              if (!pf || !promo) return "N/A";
                              const key = platPromoToKey[pf][promo];
                              return creator[key] ?? "N/A";
                            })()}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2">N/A</Typography>
                      </TableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </Box>

      {/* MODERN PAGINATION */}
      {filteredData.length > 0 && (
        <Card 
          elevation={0} 
          sx={{ 
            mt: 4, 
            p: 3, 
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: (theme) => theme.palette.mode === 'dark' 
              ? 'background.paper'
              : 'grey.25'
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Showing {page * itemsPerPage + 1} to {Math.min((page + 1) * itemsPerPage, filteredData.length)} of {filteredData.length} creators
            </Typography>
            
            <Pagination
              count={Math.ceil(filteredData.length / itemsPerPage)}
              page={page + 1}
              onChange={(e, value) => setPage(value - 1)}
              color="primary"
              shape="rounded"
              size="large"
            />
          </Stack>
        </Card>
      )}

      <AllFilters
        allFiltersOpen={allFiltersOpen}
        setAllFiltersOpen={setAllFiltersOpen}
        minFollow={minFollow}
        setMinFollow={setMinFollow}
        maxFollow={maxFollow}
        setMaxFollow={setMaxFollow}
        minViews={minViews}
        setMinViews={setMinViews}
        maxViews={maxViews}
        setMaxViews={setMaxViews}
        creatorGender={creatorGender}
        setCreatorGender={setCreatorGender}
        creatorRace={creatorRace}
        setCreatorRace={setCreatorRace}
        creatorRegion={creatorRegion}
        setCreatorRegion={setCreatorRegion}
        publicAge={publicAge}
        setPublicAge={setPublicAge}
        publicCountry={publicCountry}
        setPublicCountry={setPublicCountry}
        publicGender={publicGender}
        setPublicGender={setPublicGender}
        selectedCollection={selectedCollection}
        setSelectedCollection={setSelectedCollection}
        handleResetFilters={handleResetFilters}
        collections={collections}
      />

      {/* DIALOG: Add New Collection */}
      <Dialog
        open={isAddCollectionDialogOpen}
        onClose={() => setIsAddCollectionDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3, minWidth: 400 }
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Save />
            <Typography variant="h6">Save Collection</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Collection Name"
            fullWidth
            value={newCollectionName}
            onChange={(e) => setNewCollectionName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setIsAddCollectionDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleSaveCollection();
              setIsAddCollectionDialogOpen(false);
            }}
            disabled={!newCollectionName}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Save Collection
          </Button>
        </DialogActions>
      </Dialog>

      <ChatModalCreator
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        creatorId={chatCreatorId}
      />
    </>
  );
});

export default LeftColumnSearch;
